import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext.jsx';
import { useSettings } from '../context/SettingsContext.jsx';
import { uploadUrl } from '../api/client.js';
import { MORPH_BTN_SM } from '../lib/ui.js';
import Toggle from './Toggle.jsx';
import MarqueeText from './MarqueeText.jsx';

const NAV_ITEMS = [
  { label: 'Beranda', to: '/', icon: 'home' },
  { label: 'Layanan', to: '/layanan', icon: 'auto_awesome' },
  { label: 'Portofolio', to: '/portofolio', icon: 'grid_view' },
  { label: 'Testimoni', to: '/testimoni', icon: 'format_quote' },
  { label: 'Jadwal', to: '/jadwal', icon: 'calendar_month' },
  { label: 'Kontak', to: '/kontak', icon: 'edit_calendar' },
];

export default function Navbar() {
  const { dark, toggleDark } = useTheme();
  const { settings } = useSettings();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isArticle = location.pathname.startsWith('/artikel/');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.classList.toggle('modal-open', mobileOpen);
    return () => document.body.classList.remove('modal-open');
  }, [mobileOpen]);

  const words = (settings.site_name || 'Exotic Pet World').split(' ');
  const line2 = words.length > 1 ? words.pop().toUpperCase() : '';
  const line1 = words.join(' ').toUpperCase();
  const useLogo = settings.app_logo_mode === 'logo' && !!settings.app_logo;
  const color = settings.app_color || 'var(--accent)';

  return (
    <>
      <div
        className="fixed top-0 left-0 right-0 z-40 h-[130px] pointer-events-none transition-opacity duration-300"
        style={{
          opacity: scrolled ? 1 : 0,
          backdropFilter: scrolled ? 'blur(14px)' : 'blur(0px)',
          background: dark
            ? 'linear-gradient(to bottom, rgba(10,15,10,0.8), transparent)'
            : 'linear-gradient(to bottom, rgba(255,255,255,0.7), transparent)',
          maskImage: 'linear-gradient(to bottom, black 0%, black 55%, transparent 100%)',
        }}
      />
      <nav className="font-nav fixed top-0 left-0 right-0 z-[60] px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto grid grid-cols-[auto_1fr_auto] items-center gap-2 nav-desktop:gap-4">
          {/* Kiri: hamburger + title pill */}
          <div className="flex items-center gap-3 min-w-0">
            {isArticle ? (
              <Link
                to="/portofolio"
                className="nav-desktop:hidden w-16 h-16 inline-flex items-center justify-center rounded-full border border-white/50 dark:border-neutral-700/60 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)] text-[#475569] dark:text-[#94a3b8] transition-all duration-300 hover:rounded-2xl active:rounded-3xl active:scale-95"
              >
                <span className="material-symbols-outlined text-2xl">arrow_back</span>
              </Link>
            ) : (
              <button
                onClick={() => setMobileOpen((v) => !v)}
                className="nav-desktop:hidden w-16 h-16 inline-flex items-center justify-center rounded-full border border-white/50 dark:border-neutral-700/60 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)] text-[#475569] dark:text-[#94a3b8] transition-all duration-300 hover:rounded-2xl active:rounded-3xl active:scale-95"
              >
                <span className="material-symbols-outlined text-2xl">{mobileOpen ? 'close' : 'menu'}</span>
              </button>
            )}

            {useLogo ? (
              <>
                {/* Desktop: pill gambar full seperti sebelumnya */}
                <Link
                  to="/"
                  className="hidden nav-desktop:flex items-center h-14 w-fit flex-shrink-0 rounded-full shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)]"
                >
                  <div className="h-full w-fit rounded-full overflow-hidden">
                    <img src={uploadUrl(settings.app_logo)} alt={settings.site_name} className="h-full w-auto max-w-[260px] object-cover" />
                  </div>
                </Link>
                {/* Mobile: teks aja (marquee otomatis kalau kepanjangan) biar nggak makan banyak ruang */}
                <Link
                  to={isArticle ? '/portofolio' : '/'}
                  className="nav-desktop:hidden flex items-center h-16 max-w-[200px] min-w-0 rounded-full pl-4 pr-4 border border-white/50 dark:border-neutral-700/60 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)] flex-shrink"
                >
                  {isArticle ? (
                    <span className="font-display font-extrabold text-[13px] tracking-wide uppercase text-neutral-900 dark:text-white">Artikel</span>
                  ) : (
                    <MarqueeText
                      text={settings.site_name || 'Exotic Pet World'}
                      className="font-display font-extrabold text-[13px] tracking-wide uppercase text-neutral-900 dark:text-white"
                    />
                  )}
                </Link>
              </>
            ) : (
              <>
                {/* Desktop: tetap seperti semula */}
                <Link
                  to="/"
                  className="hidden nav-desktop:flex items-center gap-3 h-16 nav-desktop:h-14 rounded-full pl-2 pr-8 border border-white/50 dark:border-neutral-700/60 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)] w-fit flex-shrink-0"
                >
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `linear-gradient(180deg, ${color}, var(--color-forest-400))` }}
                  >
                    <span className="material-symbols-outlined text-white text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                      {settings.app_icon || 'pets'}
                    </span>
                  </div>
                  <div className="whitespace-nowrap">
                    <div className="font-display font-extrabold text-[13px] leading-tight" style={{ color: dark ? 'var(--accent-active-text-dark)' : 'var(--color-forest-700)' }}>{line1}</div>
                    {line2 && <div className="text-[13px] font-extrabold tracking-wide" style={{ color: dark ? 'var(--accent-active-text-dark)' : color }}>{line2}</div>}
                  </div>
                </Link>

                {/* Mobile: sama, kecuali di halaman artikel -> jadi tulisan "Artikel" */}
                <Link
                  to={isArticle ? '/portofolio' : '/'}
                  className="nav-desktop:hidden flex items-center gap-3 h-16 rounded-full pl-2 pr-8 border border-white/50 dark:border-neutral-700/60 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)] w-fit flex-shrink-0"
                >
                  {isArticle ? (
                    <span className="font-display font-extrabold text-[13px] tracking-wide uppercase whitespace-nowrap" style={{ color: dark ? 'var(--accent-active-text-dark)' : 'var(--color-forest-700)' }}>Artikel</span>
                  ) : (
                    <>
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: `linear-gradient(180deg, ${color}, var(--color-forest-400))` }}
                      >
                        <span className="material-symbols-outlined text-white text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                          {settings.app_icon || 'pets'}
                        </span>
                      </div>
                      <div className="whitespace-nowrap">
                        <div className="font-display font-extrabold text-[13px] leading-tight" style={{ color: dark ? 'var(--accent-active-text-dark)' : 'var(--color-forest-700)' }}>{line1}</div>
                        {line2 && <div className="text-[13px] font-extrabold tracking-wide" style={{ color: dark ? 'var(--accent-active-text-dark)' : color }}>{line2}</div>}
                      </div>
                    </>
                  )}
                </Link>
              </>
            )}
          </div>

          {/* Tengah: menu pill (desktop) */}
          <div className="hidden nav-desktop:flex items-center justify-center gap-3">
            <div className="relative flex items-center gap-1 h-14 px-2 rounded-full border border-white/50 dark:border-neutral-700/60 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)]">
              {NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`dn-link relative z-10 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[13px] font-extrabold ${
                      isActive
                        ? 'dn-active text-[var(--accent-active-text)] dark:text-[var(--accent-active-text-dark)]'
                        : 'text-neutral-700 dark:text-white hover:bg-[color-mix(in_oklab,var(--accent)_8%,transparent)] hover:text-[var(--accent)] dark:hover:bg-[rgba(126,201,90,0.12)] dark:hover:text-[var(--accent-active-text-dark)]'
                    }`}
                    style={{ backgroundColor: isActive ? (dark ? 'var(--accent-pill-bg-dark)' : 'var(--accent-pill-bg)') : 'transparent' }}
                  >
                    {isActive && (
                      <span className="material-symbols-outlined text-base" style={{ fontVariationSettings: "'FILL' 1", animation: 'navIconPop 0.45s var(--ease-spring)' }}>
                        {item.icon}
                      </span>
                    )}
                    {item.label}
                  </Link>
                );
              })}
            </div>
            <div className="relative flex items-center gap-1 h-14 px-2 rounded-full border border-white/50 dark:border-neutral-700/60 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)]">
            <Link
              to="/kontak"
              className="dn-cta hidden nav-desktop:flex items-center gap-2 px-5 h-10 rounded-[20px] text-[13px] font-extrabold text-white hover:shadow-lg hover:rounded-[14px] active:rounded-[10px] min-w-0"
              style={{ backgroundColor: color }}
            >
              <span className="material-symbols-outlined text-base flex-shrink-0">edit_calendar</span>
              <span className="min-w-0 max-w-[90px] lg:max-w-[150px] xl:max-w-none">
                <MarqueeText text="BUAT EVENT SERU" className="text-[13px] font-extrabold" />
              </span>
            </Link>
            </div>
          </div>

          {/* Kanan: darkmode + admin */}
          <div className="flex items-center gap-3 justify-self-end">
            <div className="hidden nav-desktop:flex items-center h-14 px-1 rounded-full border border-white/50 dark:border-neutral-700/60 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)]">
              <button onClick={toggleDark} className="dn-link flex items-center justify-center w-10 h-10 rounded-full text-[#475569] dark:text-[#94a3b8] hover:bg-[color-mix(in_oklab,var(--accent)_8%,transparent)] hover:text-[var(--accent)] hover:rounded-2xl active:rounded-3xl dark:hover:bg-[rgba(126,201,90,0.12)] dark:hover:text-[var(--accent-active-text-dark)]">
                <span className="material-symbols-outlined text-base">{dark ? 'light_mode' : 'bedtime'}</span>
              </button>
              <div className="w-px h-9 mx-0.5 bg-neutral-200 dark:bg-neutral-700" />
              <Link to="/admin/login" title="Panel Admin" className="dn-link flex items-center justify-center w-10 h-10 rounded-full text-[#475569] dark:text-[#94a3b8] hover:bg-[color-mix(in_oklab,var(--accent)_8%,transparent)] hover:text-[var(--accent)] hover:rounded-2xl active:rounded-3xl dark:hover:bg-[rgba(126,201,90,0.12)] dark:hover:text-[var(--accent-active-text-dark)]">
                <span className="material-symbols-outlined text-base">shield_person</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile menu trigger tetap di navbar */}
      </nav>

      {/* Mobile drawer — struktur & animasi disamakan dengan sidebar admin panel */}
      <aside className={`nav-desktop:hidden fixed top-0 left-0 h-full w-[86vw] max-w-[360px] z-[9999] flex flex-col rounded-tr-3xl rounded-br-3xl overflow-hidden bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)] transition-transform duration-300 will-change-transform ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center gap-3 px-6 py-6 border-b border-black/5 dark:border-white/10">
          {useLogo ? (
            <img src={uploadUrl(settings.app_logo)} alt={settings.site_name} className="h-11 w-auto max-w-[200px] object-contain object-left" />
          ) : (
            <>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg, ${color}, var(--color-forest-400))` }}>
                <span className="material-symbols-outlined text-white text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>{settings.app_icon || 'pets'}</span>
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold font-display truncate" style={{ color }}>{line1}</div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400 font-medium truncate">{line2}</div>
              </div>
            </>
          )}
        </div>

        <nav className="flex-1 px-4 py-5 overflow-y-auto">
          <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider px-4 mb-3 mt-1">Menu</p>
          <div className="space-y-1">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`dn-link flex items-center gap-3.5 px-4 py-3.5 rounded-xl text-base font-semibold ${MORPH_BTN_SM} ${
                    isActive
                      ? 'dn-active bg-[var(--accent-pill-bg)] text-[var(--accent-active-text)] dark:bg-[var(--accent-pill-bg-dark)] dark:text-[var(--accent-active-text-dark)]'
                      : 'text-[#262626] dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  }`}
                >
                  <span className="material-symbols-outlined text-xl" style={{ fontVariationSettings: `'FILL' ${isActive ? 1 : 0}` }}>{item.icon}</span>
                  {item.label}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="px-4 py-5 border-t border-black/5 dark:border-white/10">
          <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl text-base font-medium text-[#262626] dark:text-neutral-400">
            <span className="flex items-center gap-3.5">
              <span className="material-symbols-outlined text-lg">{dark ? 'bedtime' : 'light_mode'}</span>
              Mode Gelap
            </span>
            <Toggle checked={dark} onChange={() => toggleDark()} />
          </div>
          <Link to="/admin/login" className={`dn-link flex items-center gap-3.5 px-4 py-3.5 rounded-xl text-base font-medium text-[#262626] dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 ${MORPH_BTN_SM}`}>
            <span className="material-symbols-outlined text-lg">shield_person</span> Panel Admin
          </Link>
        </div>
      </aside>

      {mobileOpen && <div className="nav-desktop:hidden fixed inset-0 z-[9998] bg-black/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />}
    </>
  );
}